import { useState, useEffect, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n||0).toLocaleString("en-IN", { minimumFractionDigits:2 });

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00", bg:"#f3f9ec" },
  { id:"amazon",   name:"Amazon",   color:"#c45000", bg:"#fff3e8" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0", bg:"#ebf2ff" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c", bg:"#fff0f4" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089", bg:"#fdf0fb" },
];

const CLAIM_TYPES = [
  { id:"underpayment",  label:"Underpayment"   },
  { id:"missing_order", label:"Missing Order"  },
  { id:"wrong_fee",     label:"Wrong Fee"      },
  { id:"return_dispute",label:"Return Dispute" },
  { id:"other",         label:"Other"          },
];

const STATUS_COLORS = {
  matched:      { color:"#15803d", bg:"#f0fdf4" },
  underpaid:    { color:"#dc2626", bg:"#fef2f2" },
  overpaid:     { color:"#d97706", bg:"#fffbeb" },
  pending:      { color:"#6b7280", bg:"#f3f4f6" },
  disputed:     { color:"#7c3aed", bg:"#f5f3ff" },
  draft:        { color:"#6b7280", bg:"#f3f4f6" },
  submitted:    { color:"#2563eb", bg:"#eff6ff" },
  under_review: { color:"#d97706", bg:"#fffbeb" },
  approved:     { color:"#15803d", bg:"#f0fdf4" },
  rejected:     { color:"#dc2626", bg:"#fef2f2" },
  closed:       { color:"#6b7280", bg:"#f3f4f6" },
  reconciled:   { color:"#15803d", bg:"#f0fdf4" },
};

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb", whiteSpace:"nowrap" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", fontSize:12, verticalAlign:"middle" },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  kpi:     { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px" },
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" },
  modal:   { background:"#fff", borderRadius:12, padding:24, width:560, maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background:bg, color, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600, whiteSpace:"nowrap" }}>{label}</span>
);

const TEMPLATE_COLS = ["orderId","orderDate","sku","productName","qty","orderAmount","settledAmount","commission","platformFees","shippingFees","returnCharges","tds","tcs","otherDeductions","notes"];

export default function PageReconciliation({ toast }) {
  const [tab,          setTab]          = useState("upload");
  const [settlements,  setSettlements]  = useState([]);
  const [claims,       setClaims]       = useState([]);
  const [pnl,          setPnl]          = useState(null);
  const [loading,      setLoading]      = useState("");
  const [activeSettlement, setActiveSettlement] = useState(null);
  const [uploadData,   setUploadData]   = useState([]);
  const [uploadMeta,   setUploadMeta]   = useState({ platform:"amazon", period:"", fromDate:"", toDate:"", fileName:"" });
  const [claimModal,   setClaimModal]   = useState(false);
  const [claimForm,    setClaimForm]    = useState({ platform:"amazon", orderId:"", claimType:"underpayment", claimedAmount:"", description:"" });
  const [pnlDates,     setPnlDates]     = useState({ from: new Date(Date.now()-30*24*60*60*1000).toISOString().split("T")[0], to: new Date().toISOString().split("T")[0] });
  const [filterStatus, setFilterStatus] = useState("all");

  const loadSettlements = useCallback(async () => {
    setLoading("settlements");
    try {
      const data = await api("/reconciliation/settlements");
      setSettlements(data.settlements || []);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  }, [toast]);

  const loadClaims = useCallback(async () => {
    setLoading("claims");
    try {
      const data = await api("/reconciliation/claims");
      setClaims(data.claims || []);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  }, [toast]);

  const loadPnl = useCallback(async () => {
    setLoading("pnl");
    try {
      const data = await api(`/reconciliation/pnl?from=${pnlDates.from}&to=${pnlDates.to}`);
      setPnl(data);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  }, [toast, pnlDates]);

  useEffect(() => {
    loadSettlements();
    loadClaims();
  }, []);

  // ── parse Excel/CSV ───────────────────────────────────────────
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadMeta((f) => ({ ...f, fileName: file.name }));
    try {
      const XLSX = await import("xlsx");
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf);
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      // normalize column names
      const normalized = rows.map((row) => {
        const n = {};
        Object.entries(row).forEach(([k, v]) => {
          const key = k.toLowerCase().replace(/[\s_-]/g,"");
          if (key.includes("orderid") || key.includes("order_id"))         n.orderId       = String(v);
          else if (key.includes("orderdate") || key.includes("date"))      n.orderDate     = v;
          else if (key.includes("sku"))                                     n.sku           = String(v);
          else if (key.includes("product") || key.includes("item"))        n.productName   = String(v);
          else if (key.includes("qty") || key.includes("quantity"))        n.qty           = parseFloat(v)||1;
          else if (key.includes("orderamount") || key.includes("saleamount") || key.includes("totalprice")) n.orderAmount = parseFloat(v)||0;
          else if (key.includes("settled") || key.includes("netpayment") || key.includes("remittance"))     n.settledAmount = parseFloat(v)||0;
          else if (key.includes("commission"))                              n.commission    = parseFloat(v)||0;
          else if (key.includes("platformfee") || key.includes("marketplacefee")) n.platformFees = parseFloat(v)||0;
          else if (key.includes("shipping"))                               n.shippingFees  = parseFloat(v)||0;
          else if (key.includes("return"))                                 n.returnCharges = parseFloat(v)||0;
          else if (key.includes("tds"))                                    n.tds           = parseFloat(v)||0;
          else if (key.includes("tcs"))                                    n.tcs           = parseFloat(v)||0;
          else if (key.includes("other") || key.includes("deduction"))     n.otherDeductions = parseFloat(v)||0;
          else if (key.includes("note"))                                   n.notes         = String(v);
        });
        return n;
      }).filter((r) => r.orderId || r.orderAmount);

      setUploadData(normalized);
      toast(normalized.length + " rows parsed ✓", "success");
    } catch(e) { toast("Failed to parse file: " + e.message, "error"); }
  };

  const submitUpload = async () => {
    if (!uploadData.length) { toast("No data to upload", "error"); return; }
    setLoading("upload");
    try {
      const data = await api("/reconciliation/settlements/upload", {
        method: "POST",
        body: JSON.stringify({ ...uploadMeta, items: uploadData }),
      });
      toast("Reconciled! Underpaid: ₹" + data.summary.totalUnderpaid, "success");
      setUploadData([]);
      loadSettlements();
      setTab("settlements");
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  const downloadTemplate = () => {
    const csv = TEMPLATE_COLS.join(",") + "\n" +
      "ORD-001,2024-01-15,LP-BLK-001,Black Leather Belt,1,999,820,89.9,0,50,0,18,0,0,\n" +
      "ORD-002,2024-01-16,LP-BRN-001,Brown Formal Belt,2,1998,1600,179.8,0,100,0,36,0,0,\n";
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(csv);
    a.download = "settlement_template.csv";
    a.click();
    toast("Template downloaded ✓", "success");
  };

  const loadSettlementDetail = async (id) => {
    setLoading("detail_" + id);
    try {
      const data = await api("/reconciliation/settlements/" + id);
      setActiveSettlement(data.settlement);
      setTab("detail");
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  const autoClaims = async (id) => {
    setLoading("autoclaims_" + id);
    try {
      const data = await api("/reconciliation/settlements/" + id + "/auto-claims", { method:"POST", body:"{}" });
      toast(data.message, "success");
      loadClaims();
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  const deleteSettlement = async (id) => {
    try {
      await api("/reconciliation/settlements/" + id, { method:"DELETE" });
      toast("Settlement deleted ✓", "success");
      loadSettlements();
    } catch(e) { toast(e.message, "error"); }
  };

  const createClaim = async () => {
    if (!claimForm.claimedAmount) { toast("Enter claim amount", "error"); return; }
    setLoading("claim");
    try {
      await api("/reconciliation/claims", { method:"POST", body:JSON.stringify(claimForm) });
      toast("Claim created ✓", "success");
      setClaimModal(false);
      loadClaims();
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(""); }
  };

  const updateClaimStatus = async (id, status, notes) => {
    try {
      await api("/reconciliation/claims/" + id + "/status", { method:"PUT", body:JSON.stringify({ status, notes }) });
      toast("Claim updated ✓", "success");
      loadClaims();
    } catch(e) { toast(e.message, "error"); }
  };

  // summary stats
  const totalUnderpaid = settlements.reduce((a,s) => a+s.totalUnderpaid, 0);
  const totalClaimed   = claims.reduce((a,c) => a+c.claimedAmount, 0);
  const totalApproved  = claims.filter((c) => c.status==="approved").reduce((a,c) => a+c.approvedAmount, 0);
  const openClaims     = claims.filter((c) => ["draft","submitted","under_review"].includes(c.status)).length;

  const filteredItems  = activeSettlement?.items?.filter((i) => filterStatus==="all" || i.status===filterStatus) || [];

  return (
    <div>
      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
        {[
          { label:"Settlements",      value:settlements.length,      color:"#2563eb" },
          { label:"Total Underpaid",  value:INR(totalUnderpaid),     color:"#dc2626" },
          { label:"Claims Raised",    value:claims.length,           color:"#d97706" },
          { label:"Amount Recovered", value:INR(totalApproved),      color:"#15803d" },
        ].map((k) => (
          <div key={k.label} style={S.kpi}>
            <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
            <div style={{ fontSize:k.value.toString().startsWith("₹")?18:22, fontWeight:700, color:k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16, flexWrap:"wrap" }}>
        {[
          { id:"upload",      label:"⬆ Upload Settlement" },
          { id:"settlements", label:"📋 Settlements (" + settlements.length + ")" },
          { id:"claims",      label:"⚖ Claims (" + claims.length + ")" },
          { id:"pnl",         label:"📊 P&L by Platform" },
          ...(activeSettlement ? [{ id:"detail", label:"🔍 " + activeSettlement.platform + " — " + activeSettlement.period }] : []),
        ].map((t) => (
          <button key={t.id} style={S.tab(tab===t.id)} onClick={() => { setTab(t.id); if(t.id==="pnl") loadPnl(); }}>{t.label}</button>
        ))}
      </div>

      {/* ══ UPLOAD ══ */}
      {tab==="upload" && (
        <div style={{ maxWidth:700 }}>
          <div style={S.card}>
            <div style={S.cardHdr}><span>Upload Settlement File</span></div>
            <div style={{ padding:20 }}>
              {/* template */}
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:14, marginBottom:16 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:6 }}>📋 Download Template</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:10 }}>
                  Download the CSV template and fill with your settlement data from any platform.
                </div>
                <button style={{ ...S.btn("#15803d"), fontSize:12 }} onClick={downloadTemplate}>⬇ Download CSV Template</button>
              </div>

              {/* meta */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={S.label}>Platform *</label>
                  <select style={S.input} value={uploadMeta.platform}
                    onChange={(e) => setUploadMeta((f) => ({ ...f, platform:e.target.value }))}>
                    {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Period (e.g. 2024-01)</label>
                  <input style={S.input} placeholder="2024-01" value={uploadMeta.period}
                    onChange={(e) => setUploadMeta((f) => ({ ...f, period:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>From Date</label>
                  <input type="date" style={S.input} value={uploadMeta.fromDate}
                    onChange={(e) => setUploadMeta((f) => ({ ...f, fromDate:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>To Date</label>
                  <input type="date" style={S.input} value={uploadMeta.toDate}
                    onChange={(e) => setUploadMeta((f) => ({ ...f, toDate:e.target.value }))}/>
                </div>
              </div>

              <label style={S.label}>Upload Excel or CSV</label>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ marginBottom:16, fontSize:13, display:"block" }}/>

              {uploadData.length > 0 && (
                <>
                  <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:12, marginBottom:14, fontSize:13, color:"#2563eb", fontWeight:600 }}>
                    ✓ {uploadData.length} records parsed — ready to reconcile
                  </div>

                  {/* preview */}
                  <div style={{ overflowX:"auto", maxHeight:200, marginBottom:14 }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                      <thead><tr>
                        {["Order ID","Product","Qty","Order Amt","Settled Amt","Commission","TDS"].map((h) => (
                          <th key={h} style={{ ...S.th, fontSize:10 }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {uploadData.slice(0,5).map((row, i) => (
                          <tr key={i}>
                            <td style={S.td}>{row.orderId}</td>
                            <td style={S.td}>{row.productName}</td>
                            <td style={S.td}>{row.qty}</td>
                            <td style={S.td}>{INR(row.orderAmount)}</td>
                            <td style={S.td}>{INR(row.settledAmount)}</td>
                            <td style={S.td}>{INR(row.commission)}</td>
                            <td style={S.td}>{INR(row.tds)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {uploadData.length > 5 && <div style={{ textAlign:"center", padding:8, fontSize:11, color:"#9ca3af" }}>…and {uploadData.length-5} more rows</div>}
                  </div>

                  <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14, opacity:loading==="upload"?0.7:1 }}
                    onClick={submitUpload} disabled={loading==="upload"}>
                    {loading==="upload" ? "Reconciling…" : "🔍 Reconcile " + uploadData.length + " Records"}
                  </button>
                </>
              )}

              {/* platform instructions */}
              <div style={{ marginTop:20, fontWeight:600, fontSize:13, marginBottom:10 }}>How to download settlement report from each platform:</div>
              {[
                { platform:"Amazon",   steps:"Seller Central → Reports → Payments → Transaction View → Download" },
                { platform:"Flipkart", steps:"Seller Hub → Payments → Settlement Report → Download CSV" },
                { platform:"Myntra",   steps:"Partner Portal → Reports → Settlement Report → Download" },
                { platform:"Meesho",   steps:"Supplier Panel → Payments → Download Payment Report" },
                { platform:"Shopify",  steps:"Shopify Admin → Analytics → Finances → Export" },
              ].map((p) => (
                <div key={p.platform} style={{ display:"flex", gap:10, marginBottom:8, fontSize:12 }}>
                  <span style={{ fontWeight:600, minWidth:70, color:"#374151" }}>{p.platform}</span>
                  <span style={{ color:"#6b7280" }}>{p.steps}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ SETTLEMENTS LIST ══ */}
      {tab==="settlements" && (
        <div style={S.card}>
          <div style={S.cardHdr}>
            <span>Settlement History</span>
            <button style={S.btn()} onClick={loadSettlements} disabled={loading==="settlements"}>↻ Refresh</button>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Platform","Period","Orders","Order Value","Settled","Fees","Underpaid","Status","Actions"].map((h) => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {settlements.map((s) => {
                  const pl = PLATFORMS.find((p) => p.id===s.platform);
                  const st = STATUS_COLORS[s.status] || STATUS_COLORS.draft;
                  return (
                    <tr key={s._id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                      <td style={S.td}>
                        {pl && <span style={{ fontWeight:600, color:pl.color }}>{pl.name}</span>}
                      </td>
                      <td style={{ ...S.td, fontFamily:"monospace", fontSize:11 }}>{s.period}</td>
                      <td style={S.td}>{s.totalOrders}</td>
                      <td style={{ ...S.td, fontWeight:600 }}>{INR(s.totalOrderAmount)}</td>
                      <td style={{ ...S.td, color:"#15803d", fontWeight:600 }}>{INR(s.totalSettledAmount)}</td>
                      <td style={{ ...S.td, color:"#6b7280" }}>{INR(s.totalFees)}</td>
                      <td style={{ ...S.td, color:s.totalUnderpaid>0?"#dc2626":"#15803d", fontWeight:600 }}>
                        {s.totalUnderpaid > 0 ? "-" + INR(s.totalUnderpaid) : "✓ None"}
                      </td>
                      <td style={S.td}><Badge label={s.status} color={st.color} bg={st.bg}/></td>
                      <td style={S.td}>
                        <div style={{ display:"flex", gap:4 }}>
                          <button style={{ ...S.btnOut, fontSize:10 }} onClick={() => loadSettlementDetail(s._id)}
                            disabled={loading==="detail_"+s._id}>
                            {loading==="detail_"+s._id ? "…" : "View"}
                          </button>
                          {s.totalUnderpaid > 0 && (
                            <button style={{ ...S.btn("#dc2626"), fontSize:10, padding:"3px 8px" }}
                              onClick={() => autoClaims(s._id)} disabled={loading==="autoclaims_"+s._id}>
                              {loading==="autoclaims_"+s._id ? "…" : "⚖ Auto-Claim"}
                            </button>
                          )}
                          <button style={{ ...S.btnOut, fontSize:10, color:"#dc2626", borderColor:"#fecaca" }}
                            onClick={() => deleteSettlement(s._id)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {settlements.length === 0 && (
                  <tr><td colSpan={9} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>
                    No settlements yet — upload your first settlement file
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ SETTLEMENT DETAIL ══ */}
      {tab==="detail" && activeSettlement && (
        <>
          {/* summary bar */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"Total Orders",   value:activeSettlement.totalOrders, color:"#2563eb" },
              { label:"Order Value",    value:INR(activeSettlement.totalOrderAmount), color:"#374151" },
              { label:"Settled",        value:INR(activeSettlement.totalSettledAmount), color:"#15803d" },
              { label:"Platform Fees",  value:INR(activeSettlement.totalFees), color:"#6b7280" },
              { label:"Underpaid",      value:INR(activeSettlement.totalUnderpaid), color:"#dc2626" },
            ].map((k) => (
              <div key={k.label} style={S.kpi}>
                <div style={{ fontSize:10, color:"#6b7280", marginBottom:4 }}>{k.label}</div>
                <div style={{ fontSize:15, fontWeight:700, color:k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
            <span style={{ fontSize:13, fontWeight:600 }}>Filter:</span>
            {["all","matched","underpaid","overpaid","pending"].map((s) => (
              <button key={s} style={{ ...S.btn(filterStatus===s?"#F47920":"#9ca3af"), padding:"4px 12px", fontSize:11 }}
                onClick={() => setFilterStatus(s)}>
                {s.charAt(0).toUpperCase()+s.slice(1)}
              </button>
            ))}
            <span style={{ fontSize:12, color:"#9ca3af", marginLeft:"auto" }}>{filteredItems.length} items</span>
            {activeSettlement.totalUnderpaid > 0 && (
              <button style={{ ...S.btn("#dc2626") }} onClick={() => autoClaims(activeSettlement._id)}>
                ⚖ Auto-Create Claims for All Underpayments
              </button>
            )}
          </div>

          <div style={S.card}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Order ID","Product","Qty","Order Amt","Platform Fees","Expected","Settled","Difference","Status",""].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filteredItems.map((item, i) => {
                    const st = STATUS_COLORS[item.status] || STATUS_COLORS.pending;
                    const expected = item.settledAmount - item.difference;
                    return (
                      <tr key={i} style={{ background:item.status==="underpaid"?"#fff5f5":item.status==="matched"?"#f0fdf4":"" }}
                        onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"}
                        onMouseLeave={(e) => e.currentTarget.style.background=item.status==="underpaid"?"#fff5f5":item.status==="matched"?"#f0fdf4":""}>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{item.orderId}</td>
                        <td style={{ ...S.td, maxWidth:150, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.productName}</td>
                        <td style={{ ...S.td, textAlign:"center" }}>{item.qty}</td>
                        <td style={{ ...S.td, fontWeight:600 }}>{INR(item.orderAmount)}</td>
                        <td style={{ ...S.td, color:"#6b7280" }}>{INR(item.platformFees)}</td>
                        <td style={S.td}>{INR(expected)}</td>
                        <td style={{ ...S.td, color:"#15803d" }}>{INR(item.settledAmount)}</td>
                        <td style={{ ...S.td, fontWeight:700, color:item.difference<0?"#dc2626":item.difference>0?"#d97706":"#15803d" }}>
                          {item.difference < 0 ? "-" + INR(Math.abs(item.difference)) : item.difference > 0 ? "+" + INR(item.difference) : "✓"}
                        </td>
                        <td style={S.td}><Badge label={item.status} color={st.color} bg={st.bg}/></td>
                        <td style={S.td}>
                          {item.status === "underpaid" && (
                            <button style={{ ...S.btn("#dc2626"), fontSize:10, padding:"3px 8px" }} onClick={() => {
                              setClaimForm((f) => ({
                                ...f,
                                platform:      activeSettlement.platform,
                                orderId:       item.orderId,
                                claimType:     "underpayment",
                                claimedAmount: Math.abs(item.difference),
                                description:   "Order " + item.orderId + " underpaid by ₹" + Math.abs(item.difference),
                              }));
                              setClaimModal(true);
                            }}>Raise Claim</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══ CLAIMS ══ */}
      {tab==="claims" && (
        <>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}>
            <button style={S.btn()} onClick={() => setClaimModal(true)}>+ New Claim</button>
          </div>
          <div style={S.card}>
            <div style={S.cardHdr}><span>Claims Tracker</span><span style={{ fontSize:12, color:"#9ca3af" }}>{openClaims} open claims</span></div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Claim #","Platform","Order","Type","Claimed","Approved","Status","Actions"].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {claims.map((c) => {
                    const pl = PLATFORMS.find((p) => p.id===c.platform);
                    const st = STATUS_COLORS[c.status] || STATUS_COLORS.draft;
                    return (
                      <tr key={c._id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{c.claimNumber}</td>
                        <td style={S.td}>{pl && <span style={{ fontWeight:600, color:pl.color }}>{pl.name}</span>}</td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{c.orderId || "—"}</td>
                        <td style={S.td}>{CLAIM_TYPES.find((t) => t.id===c.claimType)?.label || c.claimType}</td>
                        <td style={{ ...S.td, fontWeight:600, color:"#dc2626" }}>{INR(c.claimedAmount)}</td>
                        <td style={{ ...S.td, fontWeight:600, color:"#15803d" }}>{c.approvedAmount ? INR(c.approvedAmount) : "—"}</td>
                        <td style={S.td}><Badge label={c.status.replace("_"," ")} color={st.color} bg={st.bg}/></td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:4 }}>
                            {c.status==="draft" && (
                              <button style={{ ...S.btn("#2563eb"), fontSize:10, padding:"3px 8px" }}
                                onClick={() => updateClaimStatus(c._id, "submitted")}>Submit</button>
                            )}
                            {c.status==="submitted" && (
                              <button style={{ ...S.btn("#d97706"), fontSize:10, padding:"3px 8px" }}
                                onClick={() => updateClaimStatus(c._id, "under_review")}>In Review</button>
                            )}
                            {c.status==="under_review" && (
                              <>
                                <button style={{ ...S.btn("#15803d"), fontSize:10, padding:"3px 8px" }}
                                  onClick={() => updateClaimStatus(c._id, "approved")}>Approve</button>
                                <button style={{ ...S.btn("#dc2626"), fontSize:10, padding:"3px 8px" }}
                                  onClick={() => updateClaimStatus(c._id, "rejected")}>Reject</button>
                              </>
                            )}
                            {!["closed","rejected"].includes(c.status) && (
                              <button style={{ ...S.btnOut, fontSize:10 }}
                                onClick={() => updateClaimStatus(c._id, "closed")}>Close</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {claims.length === 0 && (
                    <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>
                      No claims yet — upload a settlement to auto-detect underpayments
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══ P&L ══ */}
      {tab==="pnl" && (
        <>
          <div style={{ display:"flex", gap:10, marginBottom:14, alignItems:"center", flexWrap:"wrap" }}>
            <div>
              <label style={{ ...S.label, display:"inline", marginRight:6 }}>From</label>
              <input type="date" style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
                value={pnlDates.from} onChange={(e) => setPnlDates((f) => ({ ...f, from:e.target.value }))}/>
            </div>
            <div>
              <label style={{ ...S.label, display:"inline", marginRight:6 }}>To</label>
              <input type="date" style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
                value={pnlDates.to} onChange={(e) => setPnlDates((f) => ({ ...f, to:e.target.value }))}/>
            </div>
            <button style={{ ...S.btn(), opacity:loading==="pnl"?0.7:1 }} onClick={loadPnl} disabled={loading==="pnl"}>
              {loading==="pnl" ? "Loading…" : "📊 Load P&L"}
            </button>
          </div>

          {pnl ? (
            <>
              {/* total summary */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
                {[
                  { label:"Total Revenue",   value:INR(pnl.total.revenue),   color:"#F47920" },
                  { label:"Total Settled",   value:INR(pnl.total.settled),   color:"#15803d" },
                  { label:"Total Fees",      value:INR(pnl.total.fees),      color:"#6b7280" },
                  { label:"Total Underpaid", value:INR(pnl.total.underpaid), color:"#dc2626" },
                ].map((k) => (
                  <div key={k.label} style={S.kpi}>
                    <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
                    <div style={{ fontSize:18, fontWeight:700, color:k.color }}>{k.value}</div>
                  </div>
                ))}
              </div>

              {/* per platform */}
              <div style={S.card}>
                <div style={S.cardHdr}><span>P&L by Platform</span></div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>
                      {["Platform","Revenue","Settled","Platform Fees","Net Profit","Margin","Underpaid","Settlements"].map((h) => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {PLATFORMS.map((pl) => {
                        const p = pnl.pnl[pl.id];
                        if (!p || p.settlements === 0) return null;
                        return (
                          <tr key={pl.id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                            <td style={S.td}><span style={{ fontWeight:700, color:pl.color }}>{pl.name}</span></td>
                            <td style={{ ...S.td, fontWeight:600 }}>{INR(p.revenue)}</td>
                            <td style={{ ...S.td, color:"#15803d" }}>{INR(p.settled)}</td>
                            <td style={{ ...S.td, color:"#6b7280" }}>{INR(p.fees)}</td>
                            <td style={{ ...S.td, fontWeight:700, color:p.netProfit>=0?"#15803d":"#dc2626" }}>{INR(p.netProfit)}</td>
                            <td style={S.td}>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ flex:1, height:6, background:"#f3f4f6", borderRadius:3 }}>
                                  <div style={{ width:Math.max(0,Math.min(100,p.margin))+"%", height:6, background:p.margin>=20?"#22c55e":p.margin>=10?"#f59e0b":"#ef4444", borderRadius:3 }}/>
                                </div>
                                <span style={{ fontWeight:600, fontSize:12 }}>{p.margin}%</span>
                              </div>
                            </td>
                            <td style={{ ...S.td, color:p.underpaid>0?"#dc2626":"#15803d", fontWeight:600 }}>
                              {p.underpaid > 0 ? "-" + INR(p.underpaid) : "✓ None"}
                            </td>
                            <td style={S.td}>{p.settlements}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📊</div>
              Select a date range and click Load P&L to see platform-wise profitability
            </div>
          )}
        </>
      )}

      {/* ══ CLAIM MODAL ══ */}
      {claimModal && (
        <div style={S.overlay} onClick={(e) => e.target===e.currentTarget && setClaimModal(false)}>
          <div style={S.modal}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>Raise a Claim</span>
              <button onClick={() => setClaimModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={S.label}>Platform *</label>
                <select style={S.input} value={claimForm.platform}
                  onChange={(e) => setClaimForm((f) => ({ ...f, platform:e.target.value }))}>
                  {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Claim Type</label>
                <select style={S.input} value={claimForm.claimType}
                  onChange={(e) => setClaimForm((f) => ({ ...f, claimType:e.target.value }))}>
                  {CLAIM_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Order ID</label>
                <input style={S.input} placeholder="e.g. #1001" value={claimForm.orderId}
                  onChange={(e) => setClaimForm((f) => ({ ...f, orderId:e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Claim Amount (₹) *</label>
                <input type="number" style={S.input} placeholder="e.g. 150" value={claimForm.claimedAmount}
                  onChange={(e) => setClaimForm((f) => ({ ...f, claimedAmount:e.target.value }))}/>
              </div>
            </div>
            <label style={S.label}>Description</label>
            <textarea style={{ ...S.input, height:80, resize:"vertical" }}
              placeholder="Describe the issue…"
              value={claimForm.description}
              onChange={(e) => setClaimForm((f) => ({ ...f, description:e.target.value }))}/>
            <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14, opacity:loading==="claim"?0.7:1 }}
              onClick={createClaim} disabled={loading==="claim"}>
              {loading==="claim" ? "Creating…" : "⚖ Create Claim"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
